import * as THREE from 'three';
import EventEmitter from 'eventemitter3';

export type AnchorState = 'idle' | 'placing-ball' | 'setting-aim' | 'anchored';

export type AnchorControllerOptions = {
  /** Pose-smoothing rate (per second, framerate-independent). 0 = snap. */
  poseLerp?: number;
};

export type AnchorPose = { position: THREE.Vector3; yaw: number };

interface EventMap {
  stateChanged: (state: AnchorState) => void;
  /** Reference-space tee pose, emitted when anchoring completes. */
  anchored: (pose: AnchorPose) => void;
  /**
   * The already-anchored tee was moved — the adjust stage nudging it. Same
   * payload as 'anchored', but this one repeats freely, so listeners should do
   * placement work only and leave first-time setup to 'anchored'.
   */
  teeMoved: (pose: AnchorPose) => void;
  /** User-facing guidance: placement trouble, rejected confirms, recovery. */
  hint: (text: string) => void;
}

const RETICLE_RADIUS = 0.11;
/** A floor hit further than this from y=0 is furniture, not the mat. */
const MAX_FLOOR_OFFSET = 0.5;
/** No hit-test result for this long → fall back to head-relative placement. */
const NO_HIT_FALLBACK_MS = 2000;
/** Anchor untracked this long → track reference space instead of freezing. */
const ANCHOR_LOST_SOFT_MS = 1000;
/** Anchor untracked this long → give up and ask for a re-place. */
const ANCHOR_LOST_HARD_MS = 5000;

/**
 * How steeply a controller must point down before it counts as aiming at the
 * floor. This is what keeps an idle controller — set down on a table or the
 * floor beside the mat while the player uses their hands — from stealing the
 * reticle: a controller at rest lies roughly level, and a player pointing at
 * a ball by their feet is nowhere near level.
 */
const POINTER_MIN_DOWN = 0.15;
/**
 * How far along a controller ray a floor point is still believable. The ball is
 * at the player's feet, so a distant hit while placing is the far wall's floor
 * line, not the mat; aiming deliberately points down the range, so it needs the
 * longer leash.
 */
const POINTER_MAX_PLACE = 3;
const POINTER_MAX_AIM = 12;

/**
 * Two-step alignment of the virtual range onto the physical ball:
 * point (or look) at the real ball → confirm (tee position, XRAnchor created),
 * point downrange → confirm (yaw). While anchored, the live anchor pose drives
 * worldRoot.matrix so the virtual world stays glued to the room even as
 * tracking refines — worldRoot local space IS sim space, so physics never
 * needs to know.
 *
 * Both hands and controllers can drive it. A controller pointed at the floor
 * wins, because it is aimed deliberately and the player can see exactly where
 * from the beam; otherwise the candidate follows the player's gaze, which is
 * the only pointer a bare-handed player has.
 *
 * Designed to never dead-end: without hit-test (feature rejected, or no
 * Space Setup planes so hits never arrive) a controller falls back to
 * intersecting its own ray with the floor plane and gaze falls back to a
 * head-relative candidate, every rejected confirm explains itself via the
 * 'hint' event, and a permanently lost anchor degrades to reference-space
 * tracking before asking for a re-place.
 */
export class AnchorController extends EventEmitter<EventMap> {
  state: AnchorState = 'idle';
  worldRoot: THREE.Group;
  reticle: THREE.Mesh;
  aimArrow: THREE.Group;

  /** Parts of aimArrow, scaled independently so the head keeps its shape. */
  #aimShaft!: THREE.Mesh;
  #aimHead!: THREE.Mesh;

  #poseLerp: number;
  #session?: XRSession;
  #hitTestSource?: XRHitTestSource;
  #hitTestAvailable = false;
  /**
   * Per-controller hit-test sources, keyed by input source. Requested lazily as
   * controllers connect rather than up front: a session can start with none in
   * hand and gain them later, and requestHitTestSource needs the source's
   * targetRaySpace to exist. 'pending' and 'failed' are latched so a source is
   * only ever asked for once.
   */
  #pointerHits = new Map<XRInputSource, XRHitTestSource | 'pending' | 'failed'>();
  #anchor?: XRAnchor;
  #teePos = new THREE.Vector3();
  #yaw = 0;
  #tRel = new THREE.Matrix4();
  #hasWorldPose = false;

  /** Current placement candidate (reference space) and its provenance. */
  #candidate?: THREE.Vector3;
  #candidateHitResult?: XRHitTestResult;
  #lastHitAt = 0;
  #manualHintShown = false;
  #anchorLostAt = 0;

  // scratch
  #mAnchor = new THREE.Matrix4();
  #mWorld = new THREE.Matrix4();
  #posA = new THREE.Vector3();
  #quatA = new THREE.Quaternion();
  #sclA = new THREE.Vector3();
  #posB = new THREE.Vector3();
  #quatB = new THREE.Quaternion();
  #sclB = new THREE.Vector3();
  #fwd = new THREE.Vector3();
  #rayOrigin = new THREE.Vector3();
  #rayDir = new THREE.Vector3();
  #rayQuat = new THREE.Quaternion();
  #pointPos = new THREE.Vector3();

  constructor(scene: THREE.Scene, worldRoot: THREE.Group, options: AnchorControllerOptions = {}) {
    super();
    this.worldRoot = worldRoot;
    this.#poseLerp = options.poseLerp ?? 12; // ≈0.15/frame at 80 Hz, dt-independent

    this.reticle = this.#buildReticle();
    this.aimArrow = this.#buildAimArrow();
    scene.add(this.reticle);
    scene.add(this.aimArrow);
  }

  get isAnchored() {
    return this.state === 'anchored';
  }

  get hasAnchor() {
    return !!this.#anchor;
  }

  /** The tee is being positioned — a pointing beam is worth drawing. */
  get isPlacing() {
    return this.state === 'placing-ball' || this.state === 'setting-aim';
  }

  async begin(session: XRSession) {
    this.#disposeHitTest();
    this.#session = session;
    this.#hitTestAvailable = false;
    try {
      if (session.requestHitTestSource) {
        const viewerSpace = await session.requestReferenceSpace('viewer');
        this.#hitTestSource = await session.requestHitTestSource({ space: viewerSpace });
        this.#hitTestAvailable = true;
      }
    } catch (error) {
      console.warn('hit-test unavailable, using head-relative placement', error);
    }
    if (!this.#hitTestAvailable) {
      this.emit('hint', 'Stand at your ball and confirm to place the tee at your feet');
    }
    session.addEventListener('inputsourceschange', this.#onInputSourcesChange);
    session.addEventListener('end', () => this.#disposeHitTest());
    this.#lastHitAt = performance.now();
    this.#manualHintShown = false;
    this.#setState('placing-ball');
  }

  /** Advance the flow (wired to controller trigger / hand pinch). */
  confirm(frame: XRFrame, referenceSpace: XRReferenceSpace) {
    if (this.state === 'placing-ball') {
      if (!this.#candidate) {
        this.emit('hint', 'No spot yet — point or look down at your ball');
        return;
      }
      if (Math.abs(this.#candidate.y) > MAX_FLOOR_OFFSET) {
        this.emit('hint', 'That spot looks too high — aim at the floor by your ball');
        return;
      }
      this.#teePos.copy(this.#candidate);
      this.#createAnchor(frame, referenceSpace);
      this.#setState('setting-aim');
    } else if (this.state === 'setting-aim') {
      if (!this.#candidate) {
        this.emit('hint', 'Aim downrange to pick a target line');
        return;
      }
      const dx = this.#candidate.x - this.#teePos.x;
      const dz = this.#candidate.z - this.#teePos.z;
      if (Math.hypot(dx, dz) < 0.5) {
        this.emit('hint', 'Further downrange, then confirm to aim');
        return;
      }
      this.#yaw = Math.atan2(dx, dz);
      this.#freezeRelativeTransform(frame, referenceSpace);
      this.reticle.visible = false;
      this.aimArrow.visible = false;
      this.worldRoot.visible = true;
      this.#setState('anchored');
      this.emit('anchored', { position: this.#teePos.clone(), yaw: this.#yaw });
    }
  }

  /**
   * Place the tee outright, skipping the look-and-pinch flow.
   *
   * Immersive VR has neither of the things that flow depends on: no real floor
   * to hit-test against and no room worth anchoring to. The tee simply goes
   * where the player is standing, aimed where they were facing.
   *
   * Routed through the same state and transform a successful `confirm` ends at
   * — `#tRel` with no XRAnchor, which `#updateAnchoredPose` already handles as
   * its no-anchor case — so everything downstream of 'anchored' is identical in
   * both modes and neither has a path of its own to keep in step.
   */
  placeDirect(position: THREE.Vector3, yaw: number) {
    this.#anchor?.delete?.();
    this.#anchor = undefined;
    this.#teePos.copy(position);
    this.#yaw = yaw;
    this.#tRel.makeRotationY(yaw).setPosition(this.#teePos);
    this.#hasWorldPose = false;
    this.#anchorLostAt = 0;
    this.#candidate = undefined;
    this.#candidateHitResult = undefined;
    this.reticle.visible = false;
    this.aimArrow.visible = false;
    this.worldRoot.visible = true;
    this.#setState('anchored');
    this.emit('anchored', { position: this.#teePos.clone(), yaw: this.#yaw });
  }

  /** Where the tee currently sits (reference space) and which way it aims. */
  get teePose(): { position: THREE.Vector3; yaw: number } {
    return { position: this.#teePos.clone(), yaw: this.#yaw };
  }

  /**
   * Move the anchored tee horizontally, metres in reference space. For the
   * immersive adjust stage, where there is no real ball to place against and the
   * player is positioning a virtual one by eye.
   *
   * Only valid once anchored, and only meaningful for a directly-placed tee: with
   * a live XRAnchor the pose is owned by the runtime and `#tRel` is relative to
   * it, so nudging would fight the anchor's own tracking.
   */
  nudgeTee(dx: number, dz: number) {
    if (this.state !== 'anchored' || this.#anchor) return;
    this.#teePos.x += dx;
    this.#teePos.z += dz;
    this.#rebuildDirectTransform();
  }

  /** Rotate the aim line about the tee, degrees. Same restrictions as nudgeTee. */
  nudgeAim(degrees: number) {
    if (this.state !== 'anchored' || this.#anchor) return;
    this.#yaw += THREE.MathUtils.degToRad(degrees);
    this.#rebuildDirectTransform();
  }

  /**
   * Show the placement reticle on the tee — a marker for what is being moved
   * during the adjust stage. The reticle is otherwise only used while placing.
   */
  showTeeMarker(visible: boolean) {
    this.reticle.visible = visible;
    if (visible) {
      this.reticle.position.set(this.#teePos.x, this.#teePos.y + 0.005, this.#teePos.z);
    }
  }

  /**
   * Re-derive the world transform from teePos/yaw and republish it. Snaps rather
   * than eases: `#hasWorldPose` stays true so `#updateAnchoredPose` keeps
   * smoothing, which would otherwise make every stick step lag behind the thumb.
   */
  #rebuildDirectTransform() {
    this.#tRel.makeRotationY(this.#yaw).setPosition(this.#teePos);
    this.worldRoot.matrix.copy(this.#tRel);
    this.worldRoot.matrixWorldNeedsUpdate = true;
    this.reticle.position.set(this.#teePos.x, this.#teePos.y + 0.005, this.#teePos.z);
    // 'teeMoved', NOT 'anchored'. Same pose payload, but 'anchored' means "the
    // tee has just been established", and consumers hang first-time setup off
    // it — offering club calibration, re-solving the MR window. Firing that
    // dozens of times while a thumb is on the stick would restart those flows
    // under the player continuously.
    this.emit('teeMoved', { position: this.#teePos.clone(), yaw: this.#yaw });
  }

  /** Drop the anchor and start over (tracking drifted, player moved the mat). */
  restart() {
    this.#anchor?.delete?.();
    this.#anchor = undefined;
    this.#hasWorldPose = false;
    this.#anchorLostAt = 0;
    this.#candidate = undefined;
    this.#candidateHitResult = undefined;
    this.worldRoot.visible = false;
    if (this.state !== 'idle') {
      this.#lastHitAt = performance.now();
      this.#setState('placing-ball');
    }
  }

  /**
   * @param inputSources Live XR input sources, so a controller pointed at the
   *   floor can drive placement. Omit for gaze-only.
   */
  update(frame: XRFrame, referenceSpace: XRReferenceSpace, inputSources?: readonly (XRInputSource | undefined)[]) {
    if (this.state === 'placing-ball' || this.state === 'setting-aim') {
      this.#updatePlacement(frame, referenceSpace, inputSources);
    } else if (this.state === 'anchored') {
      this.#updateAnchoredPose(frame, referenceSpace);
    }
  }

  #updatePlacement(
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    inputSources?: readonly (XRInputSource | undefined)[],
  ) {
    const now = performance.now();
    this.#candidateHitResult = undefined;
    let candidate: THREE.Vector3 | undefined;

    // A controller pointed at the floor is the most deliberate signal there is —
    // the player can see the beam land — so it outranks gaze whenever it's aimed
    // like a pointer rather than lying idle.
    if (inputSources) {
      this.#requestPointerHits(inputSources);
      const pointed = this.#pointerCandidate(frame, referenceSpace, inputSources);
      if (pointed) {
        candidate = (this.#candidate ?? new THREE.Vector3()).copy(this.#pointPos);
        this.#candidateHitResult = pointed.hit;
        this.#lastHitAt = now;
        this.#manualHintShown = false;
      }
    }

    if (!candidate && this.#hitTestSource) {
      const results = frame.getHitTestResults(this.#hitTestSource);
      const pose = results.length ? results[0].getPose(referenceSpace) : undefined;
      if (pose) {
        const p = pose.transform.position;
        candidate = (this.#candidate ?? new THREE.Vector3()).set(p.x, p.y, p.z);
        this.#candidateHitResult = results[0];
        this.#lastHitAt = now;
        this.#manualHintShown = false;
      }
    }

    // No hits (no Space Setup planes is the common first-run case): fall back
    // to a head-relative spot on the floor so the flow can always complete.
    if (!candidate && (!this.#hitTestAvailable || now - this.#lastHitAt > NO_HIT_FALLBACK_MS)) {
      const viewerPose = frame.getViewerPose(referenceSpace);
      if (viewerPose) {
        if (this.#hitTestAvailable && !this.#manualHintShown) {
          this.#manualHintShown = true;
          this.emit('hint', 'No floor found — run Space Setup, or just pinch to place at your feet');
        }
        const head = viewerPose.transform.position;
        const o = viewerPose.transform.orientation;
        this.#fwd.set(0, 0, -1)
          .applyQuaternion(this.#quatA.set(o.x, o.y, o.z, o.w));
        this.#fwd.y = 0;
        if (this.#fwd.lengthSq() < 1e-4) this.#fwd.set(0, 0, -1);
        this.#fwd.normalize();
        const reach = this.state === 'placing-ball' ? 0.5 : 2.0;
        candidate = (this.#candidate ?? new THREE.Vector3()).set(
          head.x + this.#fwd.x * reach,
          0,
          head.z + this.#fwd.z * reach,
        );
      }
    }

    this.#candidate = candidate;

    if (!candidate) {
      this.reticle.visible = false;
      if (this.state === 'setting-aim') this.aimArrow.visible = false;
      return;
    }

    this.reticle.visible = true;
    this.reticle.position.set(candidate.x, candidate.y + 0.005, candidate.z);

    if (this.state === 'setting-aim') {
      // Arrow from the placed tee toward wherever the player is pointing, on
      // the floor
      const dx = candidate.x - this.#teePos.x;
      const dz = candidate.z - this.#teePos.z;
      const dist = Math.hypot(dx, dz);
      this.aimArrow.visible = dist > 0.3;
      if (this.aimArrow.visible) {
        this.aimArrow.position.set(this.#teePos.x, this.#teePos.y + 0.01, this.#teePos.z);
        this.aimArrow.rotation.y = Math.atan2(dx, dz);
        this.#setAimArrowLength(Math.min(dist, 3));
      }
    }
  }

  /**
   * Ask for a hit-test source per held controller, once each. Runs every
   * placement frame because controllers connect and disconnect freely — the
   * player may pick one up halfway through placing the tee.
   */
  #requestPointerHits(inputSources: readonly (XRInputSource | undefined)[]) {
    const session = this.#session;
    if (!session?.requestHitTestSource) return;
    for (const source of inputSources) {
      if (!source || (source as any).hand || this.#pointerHits.has(source)) continue;
      this.#pointerHits.set(source, 'pending');
      session.requestHitTestSource({ space: source.targetRaySpace })!
        .then(hitSource => {
          // The controller may have been put down while the request was in
          // flight — #onInputSourcesChange dropped the entry, so honour that
          // and cancel rather than resurrecting it.
          if (this.#pointerHits.get(source) !== 'pending') hitSource.cancel?.();
          else this.#pointerHits.set(source, hitSource);
        })
        .catch(() => {
          // Ray-space hit tests aren't universal; the floor-plane intersect
          // below still gives this controller a usable candidate.
          if (this.#pointerHits.get(source) === 'pending') this.#pointerHits.set(source, 'failed');
        });
    }
  }

  /**
   * Best floor point under a held controller's ray, written to `#pointPos`.
   *
   * Prefers a real hit test (which lands on the mat's actual height) and falls
   * back to intersecting the ray with y = 0 — legitimate here because the
   * session is `local-floor`, so y = 0 IS the floor. Only counts rays angled
   * down: see POINTER_MIN_DOWN.
   */
  #pointerCandidate(
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    inputSources: readonly (XRInputSource | undefined)[],
  ): { hit?: XRHitTestResult } | undefined {
    const maxDist = this.state === 'setting-aim' ? POINTER_MAX_AIM : POINTER_MAX_PLACE;
    let best: { hit?: XRHitTestResult } | undefined;
    let bestDist = Infinity;

    for (const source of inputSources) {
      if (!source || (source as any).hand) continue;
      const rayPose = frame.getPose(source.targetRaySpace, referenceSpace);
      if (!rayPose) continue;

      const o = rayPose.transform.orientation;
      this.#rayDir.set(0, 0, -1).applyQuaternion(this.#rayQuat.set(o.x, o.y, o.z, o.w));
      if (this.#rayDir.y > -POINTER_MIN_DOWN) continue; // level or raised: not pointing
      const p = rayPose.transform.position;
      this.#rayOrigin.set(p.x, p.y, p.z);

      const hitSource = this.#pointerHits.get(source);
      let hit: XRHitTestResult | undefined;
      let point: THREE.Vector3 | undefined;
      if (hitSource && hitSource !== 'pending' && hitSource !== 'failed') {
        const results = frame.getHitTestResults(hitSource);
        const hitPose = results.length ? results[0].getPose(referenceSpace) : undefined;
        if (hitPose) {
          hit = results[0];
          const h = hitPose.transform.position;
          point = this.#fwd.set(h.x, h.y, h.z); // #fwd is free until the gaze fallback
        }
      }
      if (!point) {
        const t = -this.#rayOrigin.y / this.#rayDir.y;
        if (!(t > 0)) continue;
        point = this.#fwd.copy(this.#rayOrigin).addScaledVector(this.#rayDir, t);
      }

      const dist = point.distanceTo(this.#rayOrigin);
      if (dist > maxDist || dist >= bestDist) continue;
      bestDist = dist;
      best = { hit };
      this.#pointPos.copy(point);
    }
    return best;
  }

  /** Drop hit-test sources for controllers that have gone away. */
  #onInputSourcesChange = (event: XRInputSourcesChangeEvent) => {
    for (const source of event.removed) {
      const hitSource = this.#pointerHits.get(source);
      if (hitSource && hitSource !== 'pending' && hitSource !== 'failed') hitSource.cancel?.();
      this.#pointerHits.delete(source);
    }
  };

  #createAnchor(frame: XRFrame, referenceSpace: XRReferenceSpace) {
    const onFail = (error: unknown) =>
      console.warn('Anchor creation failed, world will track reference space', error);
    try {
      if (this.#candidateHitResult?.createAnchor) {
        // Hit-test anchors track the detected surface — steadier than a pose anchor
        this.#candidateHitResult.createAnchor().then(anchor => { this.#anchor = anchor; }, onFail);
      } else if (frame.createAnchor) {
        const transform = new XRRigidTransform(
          { x: this.#teePos.x, y: this.#teePos.y, z: this.#teePos.z },
          { x: 0, y: 0, z: 0, w: 1 }
        );
        frame.createAnchor(transform, referenceSpace)?.then(anchor => { this.#anchor = anchor; }, onFail);
      }
    } catch (error) {
      onFail(error);
    }
  }

  /**
   * T_rel = T_anchor⁻¹ × T_tee, so per-frame T_world = T_anchorNow × T_rel
   * inherits the anchor's drift correction in translation and rotation.
   * Without an anchor (creation failed), T_anchor = identity and the world
   * simply tracks reference space.
   */
  #freezeRelativeTransform(frame: XRFrame, referenceSpace: XRReferenceSpace) {
    const tTee = new THREE.Matrix4()
      .makeRotationY(this.#yaw)
      .setPosition(this.#teePos);

    const anchorPose = this.#getAnchorPose(frame, referenceSpace);
    if (anchorPose) {
      this.#mAnchor.fromArray(anchorPose.transform.matrix);
      this.#tRel.copy(this.#mAnchor).invert().multiply(tTee);
    } else {
      this.#tRel.copy(tTee);
    }
    this.#hasWorldPose = false;
    this.#anchorLostAt = 0;
  }

  #updateAnchoredPose(frame: XRFrame, referenceSpace: XRReferenceSpace) {
    const anchorPose = this.#getAnchorPose(frame, referenceSpace);
    if (anchorPose) {
      this.#anchorLostAt = 0;
      this.#mAnchor.fromArray(anchorPose.transform.matrix);
      this.#mWorld.copy(this.#mAnchor).multiply(this.#tRel);
    } else if (this.#anchor) {
      // Anchor untracked. Freeze briefly (transient dropout), then degrade to
      // reference-space tracking, then ask for a re-place — never silently
      // pin the range to a stale pose forever.
      const now = performance.now();
      if (!this.#anchorLostAt) this.#anchorLostAt = now;
      const lostFor = now - this.#anchorLostAt;
      if (lostFor < ANCHOR_LOST_SOFT_MS) {
        return; // keep last pose
      }
      if (lostFor > ANCHOR_LOST_HARD_MS) {
        this.emit('hint', 'Lost tracking of your spot — pinch to re-place the tee');
        this.restart();
        return;
      }
      // soft window: rebase T_rel onto reference space from the last world
      // pose so the world stays where the player last saw it
      this.#tRel.copy(this.worldRoot.matrix);
      this.#anchor.delete?.();
      this.#anchor = undefined;
      this.#mWorld.copy(this.#tRel);
    } else {
      this.#mWorld.copy(this.#tRel);
    }

    if (!this.#hasWorldPose || this.#poseLerp <= 0) {
      this.worldRoot.matrix.copy(this.#mWorld);
      this.#hasWorldPose = true;
    } else {
      // Framerate-independent smoothing hides millimeter anchor jitter
      const t = 1 - Math.exp(-this.#poseLerp * (1 / 72));
      this.worldRoot.matrix.decompose(this.#posA, this.#quatA, this.#sclA);
      this.#mWorld.decompose(this.#posB, this.#quatB, this.#sclB);
      this.#posA.lerp(this.#posB, t);
      this.#quatA.slerp(this.#quatB, t);
      this.worldRoot.matrix.compose(this.#posA, this.#quatA, this.#sclA);
    }
    this.worldRoot.matrixWorldNeedsUpdate = true;
  }

  #getAnchorPose(frame: XRFrame, referenceSpace: XRReferenceSpace): XRPose | undefined {
    if (!this.#anchor) return undefined;
    if (frame.trackedAnchors && !frame.trackedAnchors.has(this.#anchor)) return undefined;
    try {
      return frame.getPose(this.#anchor.anchorSpace, referenceSpace) ?? undefined;
    } catch {
      return undefined;
    }
  }

  #setState(state: AnchorState) {
    if (this.state === state) return;
    this.state = state;
    this.reticle.visible = state === 'placing-ball' || state === 'setting-aim';
    if (state !== 'setting-aim') this.aimArrow.visible = false;
    this.emit('stateChanged', state);
  }

  #disposeHitTest() {
    this.#hitTestSource?.cancel?.();
    this.#hitTestSource = undefined;
    for (const hitSource of this.#pointerHits.values()) {
      if (hitSource !== 'pending' && hitSource !== 'failed') hitSource.cancel?.();
    }
    this.#pointerHits.clear();
    this.#session?.removeEventListener('inputsourceschange', this.#onInputSourcesChange);
    this.#session = undefined;
    this.#candidate = undefined;
    this.#candidateHitResult = undefined;
    this.reticle.visible = false;
    this.aimArrow.visible = false;
  }

  dispose() {
    this.#disposeHitTest();
    for (const object of [this.reticle, this.aimArrow]) {
      object.parent?.remove(object);
      object.traverse((child: any) => {
        child.geometry?.dispose?.();
        child.material?.dispose?.();
      });
    }
    this.removeAllListeners();
  }

  #buildReticle(): THREE.Mesh {
    const geometry = new THREE.RingGeometry(RETICLE_RADIUS * 0.82, RETICLE_RADIUS, 32)
      .rotateX(-Math.PI / 2);
    const dot = new THREE.CircleGeometry(RETICLE_RADIUS * 0.12, 16).rotateX(-Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: 0x21d48d,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });
    const ring = new THREE.Mesh(geometry, material);
    const dotMesh = new THREE.Mesh(dot, material);
    dotMesh.renderOrder = 10;
    ring.add(dotMesh);
    ring.renderOrder = 10;
    ring.visible = false;
    return ring;
  }

  /** Stretch the shaft to `length` m and park the head at its tip, unstretched. */
  #setAimArrowLength(length: number) {
    this.#aimShaft.scale.z = length;
    this.#aimShaft.position.z = length / 2;
    this.#aimHead.position.z = length + 0.05;
  }

  #buildAimArrow(): THREE.Group {
    const group = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({
      color: 0x21d48d,
      transparent: true,
      opacity: 0.7,
      depthTest: false,
    });
    // 1m shaft along +Z (its own scale.z follows gaze distance) + head.
    // Only the shaft stretches: scaling the whole group would stretch the
    // triangular head along Z too, up to 3x, which reads as a bent arrow.
    const shaft = new THREE.Mesh(new THREE.PlaneGeometry(0.03, 1).rotateX(-Math.PI / 2), material);
    shaft.position.z = 0.5;
    shaft.renderOrder = 10;
    // A 3-segment circle is an equilateral triangle whose first vertex sits at
    // angle 0, so after the flip into the XZ plane its apex points +X. Rotate
    // by -90° about Y to aim it down the shaft (+Z); the previous Math.PI put
    // the apex on -X, a quarter turn across the arrow.
    const head = new THREE.Mesh(
      new THREE.CircleGeometry(0.09, 3).rotateX(-Math.PI / 2).rotateY(-Math.PI / 2),
      material
    );
    head.renderOrder = 10;
    this.#aimShaft = shaft;
    this.#aimHead = head;
    group.add(shaft, head);
    this.#setAimArrowLength(1);
    group.visible = false;
    return group;
  }
}
